import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as d3 from 'd3';
import { FaqV2, MarketLineChart, MarketDonutChart, NotificationContext } from '@mdc/ui';
import { NOTIFICATION_TYPES } from '@mdc/constants';
import { CDNService, InternalNavigation, logService, dayjs, SERVICE_ERRORS } from '@mdc/services';
import marketShareFaqs from './faqs.data.js';

import './MarketShareV2.scss';

const REPORTS_FOLDER = 'reports/';

const MarketShareV2 = () => {
    const { t, ready } = useTranslation();
    const { notify } = useContext(NotificationContext);

    const [selectedVendor, setSelectedVendor] = useState();
    const [selectedTimeline, setSelectedTimeline] = useState();
    const [selectedDate, setSelectedDate] = useState();
    const [marketData, setMarketData] = useState();
    const [filteredData, setFilteredData] = useState();

    const [availableVendorNames, setAvailableVendorNames] = useState();
    const [availableDatesByVendor, setAvailableDatesByVendor] = useState();

    const descriptiveParagraph = t('The data in this report was generated on {{date}} and includes data collected in the last 10 months from users of our free tools including MetaAccess. <a href=\"https://www.opswat.com/products/metaaccess\" rel=\"noopener noreferrer\" target=\"_blank\">MetaAccess</a> prevents risky devices from accessing local networks and cloud applications using OPSWAT’s industry-leading endpoint security and advanced threat prevention technologies. The information in this report is representative of tens of millions of data points collected from over 30,000 live endpoints and includes both corporate and home users.', { date: marketData && marketData?.length ? dayjs(marketData[marketData.length - 1].date).format('YYYY-MM') : '-' });

    const onCDNerror = () => {
        notify({
            message: t('Report not available. Viewing the latest report'),
            type: NOTIFICATION_TYPES.CRITICAL
        });
    };

    const onCDNinvalid = () => {
        notify({
            message: t('There was an issue loading the report. Please try again later'),
            type: NOTIFICATION_TYPES.CRITICAL
        });
    };

    const handleCDNnavigate = (path) => {
        InternalNavigation.navigation(path);
    };

    useEffect(() => {
        (async () => {
            try {
                CDNService.on(SERVICE_ERRORS.REPORT_ERROR, onCDNerror);
                CDNService.on(SERVICE_ERRORS.INVALID_REPORT, onCDNinvalid);
                const marketInfo = await CDNService.getCDNReport('market-share-report-latest.json', REPORTS_FOLDER, handleCDNnavigate);

                if (marketInfo) {
                    const data = Object.keys(marketInfo).sort().map((date) => ({
                        date,
                        dateHumanized: dayjs(date).format('MMM YYYY'),
                        vendors: marketInfo[date].map((vendor) => ({ ...vendor, name: vendor.vendor }))
                    }));
                    setMarketData(data);
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                logService.error(err);
            }
        })();
    }, []);

    const availableTimelines = useMemo(() => marketData?.length ?
        Array(marketData.length).fill('')
            .map((v, index) => (
                {
                    title: index === 0 ? t('{{numberOfMonths}} month', { numberOfMonths: 1 }) : t('{{numberOfMonths}} months', { numberOfMonths: index + 1 }),
                    value: index + 1
                })).reverse()
        : undefined, [marketData, t]);

    useEffect(() => setSelectedTimeline(availableTimelines ? availableTimelines[0] : undefined), [availableTimelines]);

    useEffect(() => {
        if (!marketData) {
            return;
        }

        const vendorNames = [];
        const availableDates = {};

        const sliceData = selectedTimeline?.value ? marketData.length - selectedTimeline.value : 0;
        const filteredInfo = marketData.slice(sliceData);
        setFilteredData(filteredInfo);

        filteredInfo.forEach((point) => point.vendors.forEach((vendor) => {
            if (!vendorNames.includes(vendor.name)) {
                vendorNames.push(vendor.name);
            }

            availableDates[vendor.name] = { dates: [...(availableDates[vendor.name]?.dates || []), point.dateHumanized] };
        }));

        setAvailableVendorNames(vendorNames);
        setAvailableDatesByVendor(availableDates);
    }, [marketData, selectedTimeline]);

    useEffect(() => {
        if (availableVendorNames && availableDatesByVendor) {
            const firstVendor = availableVendorNames[0];
            setSelectedVendor((last) => {
                const vendor = availableVendorNames.includes(last) ? last : firstVendor;
                setSelectedDate((lastDate) => availableDatesByVendor[vendor].dates.includes(lastDate) ? lastDate : availableDatesByVendor[vendor].dates[0]);
                return vendor;
            });
        }
    }, [availableVendorNames, availableDatesByVendor]);

    const vendorColors = useMemo(() => {
        if (!availableVendorNames) {
            return {};
        }

        const colors = {};
        for (let i = 0; i < availableVendorNames.length; i++) {
            colors[availableVendorNames[i]] = d3.interpolateTurbo((i + 1) / (availableVendorNames.length || 1));
        }
        return colors;
    }, [availableVendorNames]);

    const onFocusChange = useCallback((vendorName, date) => {
        if (vendorName && date) {
            setSelectedVendor(vendorName);
            setSelectedDate(date);
        } else if (vendorName && !date) {
            setSelectedVendor(vendorName);
            setSelectedDate((lastDate) => {
                if (availableDatesByVendor[vendorName].dates.includes(lastDate)) {
                    return lastDate;
                }
                return availableDatesByVendor[vendorName].dates[availableDatesByVendor[vendorName].dates.length - 1];
            });
        } else if (!vendorName && date) {
            setSelectedDate(date);
        }
    }, [availableDatesByVendor]);

    const onTimelineChange = useCallback((title) => setSelectedTimeline(availableTimelines.find((time) => time.title === title)), [availableTimelines]);

    if (!ready) {
        return null;
    }

    return <div className='market-share-v2'>
        <h1>{t('Windows Anti-malware Market Share Report')}</h1>
        <div className='description-block'>
            <p>
                {t('OPSWAT’s streamlined Market Share Report provides monthly updates on the market share of leading anti-malware vendors and products. This report focuses on the market share of anti-malware products versus traditional antivirus engines, as they offer protection from a greater range of threats such as PUAs, ransomware, spyware, keyloggers and botnets. For each vendor represented in this report, additional data is available on the market share of the vendor’s individual products')}
            </p>
        </div>
        <div className='chart chartLine'>
            <MarketLineChart
                data={filteredData}
                vendorNames={availableVendorNames}
                vendorColors={vendorColors}
                selectedVendor={selectedVendor}
                selectedDate={selectedDate}
                onFocusChange={onFocusChange}
                timelines={availableTimelines}
                selectedTimeline={selectedTimeline?.title}
                onTimelineChange={onTimelineChange}
            />
        </div>
        <div className='chart chartDonut'>
            <MarketDonutChart
                data={filteredData}
                vendorNames={availableVendorNames}
                vendorColors={vendorColors}
                availableDates={availableDatesByVendor ? availableDatesByVendor[selectedVendor]?.dates : undefined}
                selectedVendor={selectedVendor}
                selectedDate={selectedDate}
                onFocusChange={onFocusChange}
            />
        </div>
        <div className='description-block'>
            <p>
                <span dangerouslySetInnerHTML={{ __html: descriptiveParagraph }}></span>
                <br/>
                <br/>
                {t('Although Microsoft is heavily saturated in the anti-malware product market, we have excluded this vendor because we feel that its products do not accurately represent the user\'s product of choice as they come pre-installed on many Windows systems and cannot be removed. We also have previous Market Share Reports available for comparing data from past reports. OPSWAT is committed to providing both timely and accurate information in these reports')}
            </p>
        </div>

        <FaqV2 faqTitle={t('FAQs')} faqSubTitle={t('Here are the most frequently asked questions')} faqs={marketShareFaqs(t)}/>

    </div>;
};

export default MarketShareV2;
